import React from "react";
import PropTypes from "prop-types";

import TileTip from "./TileTip";
import {
  stepBonusState, stepValueOf, stepRail, stepAtLabel, rateSuffixOf, moneyLabel, playersToGo, perCaptionOf,
} from "./bonusSteps";

/**
 * The step bonus on a Build Your Own card — what the team's reading is paying
 * right now, the lines it pays at, and the one thing that gets it to the next.
 *
 *   Team bonus                                   $250
 *   Beat the Yeti rate · 72% now            each player
 *   ━━━━━━━━━━━━━━━━●━━━━━━━●━━━━━━○━━━━━━━━━━━━
 *   ✓ 50%  $100   ✓ 70%  $250    90%  $500
 *        Per Tech        Per Tech       Per Tech
 *   2 more players ahead of the Yeti unlocks $500 each.
 *
 * Framed like the "Beating the Yeti" strip that sits just above it (same
 * border, radius, rail and type — customWidget.css `.cw-bl`), so a Beat the
 * Yeti card with teams and a bonus reads as one designed piece.
 *
 * `bonus` must already be sanitized (bonusSteps.sanitizeBonus) — the steps are
 * sorted and payable. `metric` is the live tile the bonus is paid on.
 */

const Check = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const BonusLadder = ({ bonus, metric, kind, primary, periodLabel, loading, showName }) => {
  if (!bonus || !bonus.steps || !bonus.steps.length) return null;
  const name = (metric?.title || metric?.name || "").trim() || "This metric";
  if (loading) {
    return (
      <div className="cw-bl" aria-busy="true" aria-label={`${bonus.label}: loading`}>
        <div className="cw-bl-head">
          <div className="cw-bl-lead">
            <span className="cw-bl-label">{bonus.label}</span>
            <span className="gmb-skel cw-bl-skel-sub" />
          </div>
          <span className="gmb-skel cw-bl-skel-money" />
        </div>
        <span className="gmb-skel cw-bl-skel-rail" />
      </div>
    );
  }
  if (!metric) {
    return (
      <div className="cw-bl">
        <span className="cw-bl-label">{bonus.label}</span>
        <span className="cw-bl-foot">The metric this bonus is paid on isn’t live in this workspace yet.</span>
      </div>
    );
  }

  const { steps } = bonus;
  const suffix = rateSuffixOf(metric);
  const at = (v) => stepAtLabel(v, kind, suffix);
  /* The chips drop "/hr": four of them on a quarter-width card cut "$200/hr"
     to "$200/…", and the line above and the sentence below already say it. */
  const chipAt = (v) => stepAtLabel(v, kind);
  const value = stepValueOf(metric.amount, kind);
  const st = stepBonusState(steps, value);
  const rail = stepRail(steps, value, kind);
  const team = bonus.per === "team";
  const each = team ? "" : " each";
  const payNow = moneyLabel(st.pay);
  const perCaption = perCaptionOf(bonus);

  /* The one sentence under the ladder: what gets them to the next line, in the
     metric's own terms. Beat the Yeti is a head-count, so it counts heads. */
  let foot;
  /* One line to reach and one prize is not a ladder: a box repeating the same
     two numbers under the bar says nothing the sentence doesn't. Say it as a
     sentence instead. Beat the Yeti keeps its head-count wording. */
  const single = steps.length === 1 && !metric._yetiCount;
  if (single) {
    const prize = `${moneyLabel(steps[0].pay)} bonus`;
    foot = st.done ? `Reached ${at(steps[0].at)} — the ${prize} is unlocked.` : `Reaching ${at(steps[0].at)} unlocks the ${prize}.`;
  } else if (st.done) {
    foot = `Top step reached. ${team ? "The team pot is" : "Every player is"} at the full ${payNow}.`;
  } else if (st.next) {
    const count = metric._yetiCount;
    const heads = count ? playersToGo(st.next.at, count.beating, count.players) : null;
    const prize = `${moneyLabel(st.next.pay)}${each}`;
    let more;
    const pts = Math.max(1, Math.ceil(st.gap));
    if (heads != null && heads > 0) more = `${heads} more player${heads === 1 ? "" : "s"} ahead of the Yeti`;
    else if (kind === "%") more = `${pts} more point${pts === 1 ? "" : "s"}`;
    else more = `${at(st.gap >= 1 ? Math.ceil(st.gap) : st.gap)} more`;
    foot = st.idx < 0 ? `${more} unlocks the first step, ${prize}.` : `${more} unlocks ${prize}.`;
  }

  const tip = {
    eyebrow: `${bonus.label} · ${periodLabel}`,
    rows: [
      { label: name.length > 22 ? `${name.slice(0, 21)}…` : name, value: at(value) },
      { label: "Step reached", value: st.idx >= 0 ? at(steps[st.idx].at) : "None yet" },
    ],
    verdict: {
      text: st.pay > 0 ? `Paying ${payNow}${each}` : "Not paying yet",
      tone: st.pay > 0 ? "good" : "flat",
    },
    note: team
      ? "One pot for the team, paid on the team's reading."
      : "Every player on the card earns this, paid on the team's reading.",
  };

  return (
    <div className="cw-bl" style={{ "--cw-bl-accent": primary }} role="group" aria-label={`${bonus.label}: paying ${payNow}${each}`}>
      <div className="cw-bl-head">
        <div className="cw-bl-lead">
          <span className="cw-bl-label">{bonus.label}</span>
          {/* Value first — on a narrow card the name is what gets cut. The name
              is left off when the card is already showing that metric right
              above: it only repeated the title and was the first thing to be
              clipped to "Technicia…". */}
          <span className="cw-bl-sub">{at(value)} now{showName ? ` · ${name}` : ""}</span>
        </div>
        <TileTip {...tip}>
          <span className="cw-bl-money">
            <b>{payNow}</b>
            {/* A pot is one figure for the whole team and needs no caption;
                "each player" is what says the number is per head. */}
            {!team && <span>each player</span>}
          </span>
        </TileTip>
      </div>

      <div className="cw-bl-track" aria-hidden>
        <div className="cw-bl-rail">
          <div className="cw-bl-fill" style={{ width: `${rail.valueX}%` }} />
        </div>
        {rail.marks.map((x, i) => (
          <i
            key={steps[i].at}
            className={`cw-bl-mark${i <= st.idx ? " on" : ""}`}
            /* A step on the very end would hang half off the rail. */
            style={{ left: `clamp(5px, ${x}%, calc(100% - 5px))` }}
          />
        ))}
      </div>

      {/* Step boxes and the next-step line are on unless the admin switched
          them off (bonus.hideSteps / hideNext — see bonusSteps.js). */}
      {!bonus.hideSteps && !single && (
        <div className="cw-bl-steps" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
          {steps.map((s, i) => {
            let cls = "cw-bl-step";
            if (i <= st.idx) cls += " hit";
            else if (i === st.idx + 1) cls += " next";
            return (
              <div key={s.at} className={cls}>
                <span className="cw-bl-at">
                  {i <= st.idx && <Check />}
                  {chipAt(s.at)}
                </span>
                <span className="cw-bl-pay">{moneyLabel(s.pay)}</span>
                {perCaption && <span className="cw-bl-per">{perCaption}</span>}
              </div>
            );
          })}
        </div>
      )}

      {foot && !bonus.hideNext && <div className="cw-bl-foot">{foot}</div>}
    </div>
  );
};

BonusLadder.propTypes = {
  bonus: PropTypes.shape({
    label: PropTypes.string,
    per: PropTypes.oneOf(["player", "team"]),
    steps: PropTypes.arrayOf(PropTypes.shape({ at: PropTypes.number, pay: PropTypes.number })),
    hideSteps: PropTypes.bool,
    hideNext: PropTypes.bool,
    perLabel: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  metric: PropTypes.object,
  kind: PropTypes.oneOf(["$", "%", "#"]),
  primary: PropTypes.string,
  periodLabel: PropTypes.string,
  loading: PropTypes.bool,
  showName: PropTypes.bool,
};
BonusLadder.defaultProps = {
  bonus: null, metric: null, kind: "#", primary: "#1B598E", periodLabel: "this month", loading: false, showName: true,
};

export default BonusLadder;
